import type {
  CareActionResult,
  GridPosition,
  PlantInstanceId,
  PlantState,
  Season,
} from "../types";
import type { GardenGrid } from "../entities/GardenGrid";
import type { ParticleSystem } from "../engine/ParticleSystem";
import type { Camera } from "../engine/Camera";
import type { EconomySystem } from "./EconomySystem";
import type { AudioSystem } from "./AudioSystem";
import type { QuestSystem } from "./QuestSystem";
import { getPlantDefinition } from "../entities/PlantDefinitions";
import { DECORATION_DEFINITIONS } from "../entities/Decoration";
import { initialStage } from "../entities/PlantGrowth";
export type CareCommand =
  | "water"
  | "weed"
  | "pest"
  | "fertilize"
  | "prune"
  | "seed"
  | "mulch"
  | "winterCover"
  | "harvest"
  | "remove"
  | "sweepLeaves";
export interface CareOptions {
  speciesId?: string;
  season: Season;
  tick: number;
}
const clamp = (v: number) => Math.max(0, Math.min(100, v));
const GROWTH_BONUS_COOLDOWN_TICKS = 60;
export class CareSystem {
  constructor(
    private grid: GardenGrid,
    private plants: PlantState[],
    private economy: EconomySystem,
    private quests: QuestSystem,
    private particles: ParticleSystem,
    private camera: Camera,
    private audio: AudioSystem,
  ) {}
  execute(
    action: CareCommand,
    position: GridPosition,
    options: CareOptions,
  ): CareActionResult {
    const cell = this.grid.get(position.x, position.y);
    const plantId = cell?.occupant?.kind === "plant" ? cell.occupant.plantInstanceId : undefined;
    const plant = plantId ? this.plants.find((candidate) => candidate.instanceId === plantId) : undefined;
    let success = false,
      message = "ここでは実行できません。",
      gp = 0;
    const ids: PlantInstanceId[] = [];
    if (!cell) return this.result(action, false, "庭の外です。", 0, [], []);
    switch (action) {
      case "water":
        if (
          ["soil", "grass"].includes(cell.terrain)
        ) {
          if (cell.soilMoisture >= 85) {
            cell.soilMoisture = clamp(cell.soilMoisture + 15);
            if (plant) {
              plant.soilMoisture = cell.soilMoisture;
              plant.health = clamp(plant.health - 8);
            }
            success = true;
            message = "💧 水のあげすぎです！（根腐れ注意）土が過湿状態になっています";
            gp = 0;
          } else {
            cell.soilMoisture = clamp(cell.soilMoisture + 35);
            if (plant) {
              plant.soilMoisture = cell.soilMoisture;
              plant.health = clamp(plant.health + 3);
            }
            success = true;
            message = "たっぷり水をあげました。";
            gp = 2;
          }
        }
        break;
      case "weed":
        if (cell.hasWeeds || cell.weedPressure > 0) {
          cell.hasWeeds = false;
          cell.weedPressure = 0;
          if (plant) plant.weedPressure = 0;
          success = true;
          message = "雑草をきれいに抜きました。";
          gp = 4;
        } else if (plant && plant.stage === "withered") {
          this.grid.removeAt(position.x, position.y);
          const index = this.plants.findIndex(
            (candidate) => candidate.instanceId === plant.instanceId,
          );
          if (index >= 0) this.plants.splice(index, 1);
          this.economy.addGP(2);
          this.quests.record({
            action: this.questAction(action),
            season: options.season,
          });
          return this.result(
            action,
            true,
            "🧤 枯れた植物を片付けて土を整えました。",
            2,
            [],
            [position],
          );
        }
        break;
      case "pest":
        if (plant && plant.pestLevel > 0) {
          plant.pestLevel = clamp(plant.pestLevel - 45);
          plant.health = clamp(plant.health + 6);
          success = true;
          message = "害虫を追い払いました！";
          gp = 7;
        }
        break;
      case "fertilize":
        if (["soil", "grass"].includes(cell.terrain)) {
          if (cell.fertility >= 80) {
            cell.fertility = clamp(cell.fertility + 10);
            if (plant) {
              plant.fertility = cell.fertility;
              plant.health = clamp(plant.health - 12);
            }
            success = true;
            message = "🧪 肥料のあげすぎです！（肥料やけ）濃度が高すぎて根が傷みました";
            gp = 0;
          } else {
            cell.fertility = clamp(cell.fertility + 25);
            if (plant) {
              plant.fertility = cell.fertility;
              plant.health = clamp(plant.health + 8);
            }
            success = true;
            message = "土がふかふかになりました。";
            gp = 2;
          }
        }
        break;
      case "prune":
        if (
          plant &&
          ["shrub", "tree"].includes(
            getPlantDefinition(plant.speciesId)?.category ?? "",
          )
        ) {
          if (plant.pruneStagePruned) {
            plant.health = clamp(plant.health - 15);
            success = true;
            message = "⚠️ 剪定のしすぎです！植物が傷ついてしまいました";
            gp = 0;
          } else {
            plant.pruneStagePruned = true;
            plant.pruned = true;
            plant.health = clamp(plant.health + 12);
            success = true;
            message = "✂️ 枝を整えて風通しが良くなりました！";
            gp = 8;
          }
        }
        break;
      case "seed": {
        const species =
            options.speciesId ??
            Object.keys(this.economy.snapshot.seeds).find(
              (id) => (this.economy.snapshot.seeds[id] ?? 0) > 0,
            ),
          definition = species ? getPlantDefinition(species) : undefined;
        if (
          definition &&
          definition.seasonsAvailable.includes(options.season) &&
          this.grid.canPlace(position, definition.footprint, "plant") &&
          this.economy.consumeSeed(species as string)
        ) {
          const p: PlantState = {
            instanceId: crypto.randomUUID() as PlantInstanceId,
            speciesId: species as string,
            stage: initialStage(definition),
            stageProgress: 0,
            soilMoisture: cell.soilMoisture,
            health: 100,
            pestLevel: 0,
            weedPressure: cell.weedPressure,
            fertility: cell.fertility,
            pollinated: false,
            mulched: false,
            winterProtected: false,
            pruned: false,
            plantedAtTick: options.tick,
            lastCareTick: options.tick,
            ageTicks: 0,
            harvestCount: 0,
            coldDamage: 0,
            gridPosition: { ...position },
          };
          this.plants.push(p);
          this.grid.placePlant(p, definition.footprint);
          ids.push(p.instanceId);
          success = true;
          message = `${definition.displayName}の種をまきました。`;
          gp = 3;
        } else
          message = definition
            ? "今の季節または場所には植えられません。"
            : "種を持っていません。";
        break;
      }
      case "mulch":
        if (plant && !plant.mulched) {
          plant.mulched = true;
          success = true;
          message = "根元をマルチングしました。";
          gp = 5;
        }
        break;
      case "winterCover":
        if (plant && !plant.winterProtected) {
          plant.winterProtected = true;
          success = true;
          message = "雪除けコモを設置しました。";
          gp = 6;
        }
        break;
      case "harvest": {
        const def = plant ? getPlantDefinition(plant.speciesId) : undefined;
        if (plant && def && plant.stage === "fruiting") {
          plant.harvestCount += 1;
          plant.stageProgress = 0;
          plant.stage = def.regrowsAfterHarvest ? "flowering" : "harvested";
          success = true;
          message = `${def.displayName}を収穫しました！`;
          gp = def.harvestGP;
        }
        break;
      }
      case "remove": {
        const occupant = cell.occupant;
        if (!occupant) {
          message = "取り除く対象がありません";
          break;
        }
        if (occupant.kind === "plant" && plant) {
          const def = getPlantDefinition(plant.speciesId);
          const name = def?.displayName ?? plant.speciesId;
          if (plant.stage === "seed") {
            this.economy.addSeed(plant.speciesId, 1);
            // 種まき時に得た+3GPを相殺してGP増殖ループを防止
            this.economy.spendGP(3);
            message = `🌱 ${name}の種を回収しました`;
          } else {
            gp = def?.compostGP ?? 1;
            message = `♻️ ${name}を堆肥化し、+${gp} GPを獲得しました`;
          }
          this.grid.removeAt(position.x, position.y);
          const index = this.plants.findIndex(
            (candidate) => candidate.instanceId === plant.instanceId,
          );
          if (index >= 0) this.plants.splice(index, 1);
          success = true;
        } else if (occupant.kind === "decoration") {
          const removed = this.economy.removeDecoration(occupant.decorationInstanceId);
          if (removed) {
            this.grid.removeAt(position.x, position.y);
            const def = DECORATION_DEFINITIONS[removed.decorationId as keyof typeof DECORATION_DEFINITIONS];
            success = true;
            message = `📦 ${def?.displayName ?? removed.decorationId}を取り外してインベントリに回収しました`;
          } else {
            message = "装飾が見つかりませんでした";
          }
        }
        break;
      }
      case "sweepLeaves":
        if (cell.fallenLeaves > 0) {
          cell.fallenLeaves = 0;
          success = true;
          message = "落ち葉をきれいに掃きました。";
          gp = 2;
        }
        break;
    }
    if (plant) ids.push(plant.instanceId);
    if (success) {
      if (
        plant &&
        gp > 0 &&
        ["water", "fertilize", "weed", "pest", "mulch", "prune"].includes(action) &&
        options.tick - plant.lastCareTick >= GROWTH_BONUS_COOLDOWN_TICKS
      ) {
        plant.stageProgress = clamp(plant.stageProgress * 100 + 8) / 100;
        message += " 成長が8%進みました！";
      }
      plant && (plant.lastCareTick = options.tick);
      this.economy.addGP(gp);
      this.quests.record({
        action: this.questAction(action),
        season: options.season,
      });
      const s = this.camera.gridToScreen(
        position.x,
        position.y,
        cell.elevation,
      );
      if (action === "water") this.particles.emitWatering(s.x, s.y);
      else if (action === "fertilize" || action === "harvest")
        this.particles.emitFertilizer(s.x, s.y);
      else this.particles.emitPestStars(s.x, s.y, action === "pest" ? 12 : 5);
      this.particles.emitGP(s.x, s.y - 24, gp);
      this.audio.play(action);
    }
    return this.result(action, success, message, gp, ids, [position]);
  }
  private questAction(a: CareCommand) {
    return (
      {
        weed: "removeWeeds",
        pest: "removePests",
        seed: "inspect",
        winterCover: "protectFromWinter",
        water: "water",
        fertilize: "fertilize",
        prune: "prune",
        mulch: "mulch",
        harvest: "harvest",
        remove: "inspect",
        sweepLeaves: "sweepLeaves",
      } as const
    )[a];
  }
  private result(
    action: CareCommand,
    success: boolean,
    message: string,
    gpAwarded: number,
    affectedPlantIds: PlantInstanceId[],
    affectedCells: GridPosition[],
  ): CareActionResult {
    return {
      action: this.questAction(action),
      success,
      message,
      gpAwarded,
      affectedPlantIds: [...new Set(affectedPlantIds)],
      affectedCells,
    };
  }
}
